import {
    Box,
    Button,
    ControlEcheqsList,
    Dropdown,
    DropdownItem,
    Icon,
    Text,
    Tooltip,
} from "@ui-kit/components/index.es";
import Head from "pages/_components/Head";
import Notification from "pages/_components/Notification";
import { resizableRoute } from "pages/_components/Resizable";
import { arrayOf, bool, func, number, shape } from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { actions as checksActions, selectors as checksSelectors } from "reducers/checks";
import * as config from "util/config";
import * as i18n from "util/i18n";
import * as Yup from "yup";

import { push } from "react-router-redux";

import { getPostLoginHomePath } from "util/v3UxConfig";
import {
    applyEmbeddedV3ShellLayoutState,
    clearEmbeddedV3ShellLayoutState,
    isUxDashboardImprovementsEnabled,
} from "util/v3ShellLayoutSync";

function EcheqsControl(props) {
    const {
        dispatch,
        echeqs,
        loading,
        isDesktop,
        lastUpdate,
        requestDate,
        totalCount,
        totalAmount,
        totalPages,
        pageNumber,
        isFetching,
        hasCacheRequest,
        downloadLoading,
        embeddedInV3Shell,
    } = props;

    const echeqUSDfuncionalites = config.getBoolean("frontend.ECHEQsUSD.functionalities", false);
    const emittedSectionEnabled = config.getBoolean("frontend.ControlEcheqsList.emitted.functionalities", false);
    const multipleStatusEnabled = config.getBoolean("echeq.config.multipleStatus.enabled", false);
    const getSectionStates = (section) => {
        const sectionStatesMap = {
            availables: ["ACTIVO", "ACTIVO-PENDIENTE", "CESION-PENDIENTE", "EMITIDO-PENDIENTE"],
            collections: ["CUSTODIA", "DEPOSITADO"],
            liquidations: ["PAGADO", "RECHAZADO"],
            emitted: [
                "ACTIVO",
                "ACTIVO-PENDIENTE",
                "ANULADO",
                "CADUCADO",
                "CESION-PENDIENTE",
                "CUSTODIA",
                "DEPOSITADO",
                "DEVOLUCION-PENDIENTE",
                "EMITIDO-PENDIENTE",
                "NEGOCIACION-PENDIENTE",
                "NEGOCIACION",
                "PAGADO",
                "PRESENTADO",
                "RECHAZADO",
                "REPUDIADO",
            ],
        };
        return sectionStatesMap[section] || [];
    };
    const resolveStatusFilter = (statusFormValue, section) => {
        const sectionStates = getSectionStates(section);
        if (!multipleStatusEnabled) {
            if (!statusFormValue || statusFormValue.value === "all") {
                return { status: sectionStates, statusAllSelected: true };
            }
            return { status: [statusFormValue.value], statusAllSelected: false };
        }
        if (!Array.isArray(statusFormValue) || statusFormValue.length === 0) {
            return { status: sectionStates, statusAllSelected: true };
        }
        return {
            status: statusFormValue.map((item) => item.value),
            statusAllSelected: false,
        };
    };
    const parseAmount = (amount) => {
        if (!amount || amount === "") {
            return 0;
        }

        let cleanAmount = amount.toString();

        if (cleanAmount.includes(",")) {
            const parts = cleanAmount.split(",");
            if (parts.length === 2) {
                const integerPart = parts[0].replace(/\./g, "");
                const decimalPart = parts[1];
                cleanAmount = `${integerPart}.${decimalPart}`;
            }
        } else {
            cleanAmount = cleanAmount.replace(/\./g, "");
        }

        const numericAmount = parseFloat(cleanAmount);
        return Number.isNaN(numericAmount) ? 0 : numericAmount;
    };

    const formatFiltersForAPI = (filters) => {
        const formatDate = (date) => {
            if (!date) {
                return null;
            }
            return date.toISOString().split("T")[0]; // Formato YYYY-MM-DD
        };

        const formatAmount = (amount) => {
            if (!amount || amount === "") {
                return null;
            }

            let cleanAmount = amount.toString();

            if (cleanAmount.includes(",")) {
                const parts = cleanAmount.split(",");
                if (parts.length === 2) {
                    const integerPart = parts[0].replace(/\./g, "");
                    const decimalPart = parts[1];
                    cleanAmount = `${integerPart}.${decimalPart}`;
                }
            } else {
                cleanAmount = cleanAmount.replace(/\./g, "");
            }

            const numericAmount = parseFloat(cleanAmount);
            if (Number.isNaN(numericAmount)) {
                return null;
            }
            return numericAmount;
        };

        return {
            ...filters,
            creationDateFrom: formatDate(filters.creationDateFrom),
            creationDateTo: formatDate(filters.creationDateTo),
            paymentDateFrom: formatDate(filters.paymentDateFrom),
            paymentDateTo: formatDate(filters.paymentDateTo),
            amountFrom: formatAmount(filters.amountFrom),
            amountTo: formatAmount(filters.amountTo),
        };
    };

    const getInitialFilters = () => {
        const creationDateFrom = new Date();
        creationDateFrom.setDate(creationDateFrom.getDate() - 7);

        const creationDateTo = new Date();

        const paymentDateFrom = new Date();

        const paymentDateTo = new Date();
        paymentDateTo.setDate(paymentDateTo.getDate() + 7);

        return {
            reference: "",
            amountFrom: 0,
            amountTo: 0,
            orderBy: "paymentDate",
            order: "DESC",
            creationDateFrom,
            creationDateTo,
            paymentDateFrom,
            paymentDateTo,
            status: getSectionStates("availables"),
            statusAllSelected: true,
            currency: "032",
            section: "availables",
        };
    };

    const getFormInitialValues = () => {
        const initialFilters = getInitialFilters();
        return {
            creationDates: [initialFilters.creationDateFrom, initialFilters.creationDateTo],
            paymentDates: [initialFilters.paymentDateFrom, initialFilters.paymentDateTo],
            reference: initialFilters.reference,
            status: multipleStatusEnabled ? [] : { value: "all", label: "Todos" },
            amountFrom: initialFilters.amountFrom,
            amountTo: initialFilters.amountTo,
            currency: "032",
            orderBy: initialFilters.orderBy,
            order: initialFilters.order,
        };
    };

    const [filters, setFilters] = useState(getInitialFilters);

    useEffect(() => {
        dispatch(
            checksActions.listEcheqsControl({
                pageNumber: 1,
                ...formatFiltersForAPI(filters),
            }),
        );
    }, [dispatch, filters]);

    useEffect(() => {
        let intervalId;
        const refreshInterval = config.getInteger("backend.echeq.async.refreshInterval");

        if (isFetching) {
            intervalId = setInterval(() => {
                dispatch(
                    checksActions.listEcheqsControl({
                        pageNumber,
                        ...formatFiltersForAPI(filters),
                    }),
                );
            }, refreshInterval * 1000);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [isFetching, dispatch, pageNumber, filters]);

    // maximo de 15 enteros y 2 decimales + puntos y comas = 22 caracteres
    const maxLength = 22;

    const handleBack = useCallback(() => {
        if (embeddedInV3Shell) {
            dispatch(push(getPostLoginHomePath(isUxDashboardImprovementsEnabled())));
        } else {
            dispatch(push("/desktop"));
        }
    }, [dispatch, embeddedInV3Shell]);

    const getLabels = (key) => i18n.get(`echeqs.control.${key}.label`);

    const getCardLabel = (key) => i18n.get(`echeqs.control.card.${key}.label`);

    const getDateTimeFromLastUpdate = (lastUpdateString, fallbackDate) => {
        const dateToUse = lastUpdateString || fallbackDate;
        if (!dateToUse) {
            return { date: null, hour: null };
        }
        const [date, hour] = dateToUse.split(" ");
        return { date, hour };
    };

    const { date: lastUpdateDate, hour: lastUpdateHour } = getDateTimeFromLastUpdate(lastUpdate, requestDate);

    const handleSectionChange = () => {
        if (!isDesktop) {
            dispatch(checksActions.clearEcheqsControl());
        }
    };

    const handlePageChange = (newPage) => {
        if (!isDesktop) {
            dispatch(
                checksActions.listEcheqsControlMore({
                    pageNumber: newPage,
                    ...formatFiltersForAPI(filters),
                }),
            );
        } else {
            dispatch(
                checksActions.listEcheqsControl({
                    pageNumber: newPage,
                    ...formatFiltersForAPI(filters),
                }),
            );
        }
    };

    const handleUpdate = () => {
        const dateFrom = new Date();
        const maxDaysBefore = config.get("backend.echeq.async.maxDaysBefore");
        dateFrom.setDate(dateFrom.getDate() - maxDaysBefore);

        const dateTo = new Date();

        dispatch(checksActions.requestUpdateEcheqsControl(dateFrom, dateTo));
    };

    const handleDownload = useCallback(
        (format) => {
            dispatch(checksActions.downloadEcheqsControl(format, formatFiltersForAPI(filters)));
        },
        [dispatch, filters],
    );

    const renderDesktopRightContent = useCallback(
        () => (
            <Box ml={12} align="center" gap={12}>
                <Tooltip
                    label={config.get("frontend.TooltipDownload.ControlEcheqs.functionalities")}
                    arrowPlacement="center"
                    placement="left"
                    type="Info">
                    <Icon name="Info" variant="outline" size={24} color="primary-color" />
                </Tooltip>
                <Dropdown
                    w="100%"
                    direction="right"
                    optionWidth="200px"
                    renderWithPortal
                    label={
                        <Button
                            isInactive={!hasCacheRequest}
                            aria-label={i18n.get("echeqs.control.download")}
                            text={i18n.get("echeqs.control.download")}
                            variant="outline"
                            icon="SendDown"
                            iconVariant="outline"
                            w="200px"
                        />
                    }>
                    <DropdownItem>
                        <Button
                            onClick={() => {
                                handleDownload("pdf");
                            }}
                            type="button"
                            text={<Text variant="body2">{i18n.get("global.filetype.pdf")}</Text>}
                            css={{
                                background: "transparent",
                                border: "none",
                                width: "100%",
                                padding: 12,
                                display: "flex",
                                justifyContent: "center",
                                borderBottom: `1px solid rgba(224, 224, 224, 1)`,
                                borderRadius: 0,
                                "&:hover": {
                                    backgroundColor: "rgba(242, 248, 253, 1)",
                                },
                            }}
                        />
                        <Button
                            onClick={() => {
                                handleDownload("xls");
                            }}
                            type="button"
                            text={<Text variant="body2">{i18n.get("global.filetype.xls")}</Text>}
                            css={{
                                background: "transparent",
                                border: "none",
                                width: "100%",
                                padding: 12,
                                display: "flex",
                                justifyContent: "center",
                                borderBottom: `1px solid rgba(224, 224, 224, 1)`,
                                borderRadius: 0,
                                "&:hover": {
                                    backgroundColor: "rgba(242, 248, 253, 1)",
                                },
                            }}
                        />
                        <Button
                            onClick={() => {
                                handleDownload("csv");
                            }}
                            type="button"
                            text={<Text variant="body2">{i18n.get("global.filetype.csv")}</Text>}
                            css={{
                                background: "transparent",
                                border: "none",
                                width: "100%",
                                padding: 12,
                                display: "flex",
                                justifyContent: "center",
                                borderRadius: 0,
                                "&:hover": {
                                    backgroundColor: "rgba(242, 248, 253, 1)",
                                },
                            }}
                        />
                    </DropdownItem>
                </Dropdown>
            </Box>
        ),
        [hasCacheRequest, handleDownload],
    );

    const mobileShellRightDownload = useMemo(
        () => (
            <Button
                type="button"
                variant="text"
                color="white"
                icon="SendDown"
                iconVariant="outline"
                size="large"
                isInactive={!hasCacheRequest || downloadLoading}
                aria-label={i18n.get("echeqs.control.download")}
                onClick={() => handleDownload("pdf")}
            />
        ),
        [hasCacheRequest, downloadLoading, handleDownload],
    );

    useEffect(() => {
        if (!embeddedInV3Shell) {
            return undefined;
        }
        applyEmbeddedV3ShellLayoutState(dispatch, {
            embeddedInV3Shell,
            isDesktop,
            scopes: ["echeqsControl"],
            titleKey: "echeqs.control.title",
            goBackPath: getPostLoginHomePath(isUxDashboardImprovementsEnabled()),
            rightContentMobile: !isDesktop ? mobileShellRightDownload : undefined,
        });
        return () => {
            clearEmbeddedV3ShellLayoutState(dispatch);
        };
    }, [dispatch, embeddedInV3Shell, isDesktop, mobileShellRightDownload]);

    const showLegacyHead = !embeddedInV3Shell || isDesktop;

    return (
        <>
            {!embeddedInV3Shell && <Notification scopeToShow="echeqsControl" />}
            {showLegacyHead && (
                <Box className="admin-detail-head echeqs--control__v3-layout" px={isDesktop ? 40 : 24}>
                    {isDesktop && <Head onBack={handleBack} />}
                    <Head
                        title="echeqs.control.title"
                        onBack={!isDesktop && handleBack}
                        titleClassName={
                            !isDesktop ? "header-mobile-title-background-blue overflow-visible" : "my-3 align-center"
                        }
                        headerClassName={!isDesktop ? "blue-main-header-mobile" : ""}
                        rightContent={isDesktop && renderDesktopRightContent}
                        uniqueDownload={!isDesktop}
                        {...(isDesktop && {
                            messageTooltip: config.get("frontend.TooltipInformative.ControlEcheqs.functionalities"),
                            hintText: config.get("frontend.TooltipInformative.ControlEcheqs.functionalities"),
                            hintClassName: "max-width-37rem",
                        })}
                        alwaysShowMessage={isDesktop}
                        exportDisabled={!hasCacheRequest}
                        handleClick={handleDownload}
                        downloadImageWhite={!isDesktop}
                        handleClickMessage="echeqs.control.download"
                        isFetchingExport={downloadLoading}
                    />
                </Box>
            )}
            <Box pt={!isDesktop ? 24 : 0} px={!isDesktop ? 16 : 40}>
                <ControlEcheqsList
                    loading={loading}
                    emptyStateText={i18n.get("echeqs.control.empty.message")}
                    echeqsList={echeqs || []}
                    paginationProps={{
                        action: (newPage) => {
                            handlePageChange(newPage);
                        },
                        pageNumber,
                        totalPages,
                    }}
                    getLabels={getLabels}
                    getCardLabel={getCardLabel}
                    processText={
                        hasCacheRequest
                            ? i18n.get("echeqs.control.process.text", "", {
                                  DATE: lastUpdateDate,
                                  HOUR: lastUpdateHour,
                              })
                            : i18n.get("echeqs.control.process.text.noData")
                    }
                    processAction={handleUpdate}
                    processLoading={isFetching}
                    processTextLoading={i18n.get("echeqs.control.process.text.loading")}
                    hintText={config.get("frontend.TooltipInformative.ControlEcheqs.functionalities")}
                    onChageSectionOption={handleSectionChange}
                    totalEcheqs={totalCount}
                    totalEcheqsAmount={totalAmount}
                    disabledSearch={!hasCacheRequest}
                    enableFilters
                    initialValues={getFormInitialValues()}
                    validationSchema={Yup.lazy((values) =>
                        Yup.object().shape({
                            amountFrom:
                                values.amountTo && parseAmount(values.amountTo) > 0
                                    ? Yup.string()
                                          .nullable()
                                          .test(
                                              "amount-from-validation",
                                              i18n.get("echeqs.control.validation.amountFrom"),
                                              (value) => {
                                                  if (!value || parseAmount(value) === 0) {
                                                      return true;
                                                  }
                                                  return parseAmount(value) <= parseAmount(values.amountTo);
                                              },
                                          )
                                    : Yup.string().nullable(),
                            amountTo:
                                values.amountFrom && parseAmount(values.amountFrom) > 0
                                    ? Yup.string()
                                          .nullable()
                                          .test(
                                              "amount-to-validation",
                                              i18n.get("echeqs.control.validation.amountTo"),
                                              (value) => {
                                                  if (!value || parseAmount(value) === 0) {
                                                      return true;
                                                  }
                                                  return parseAmount(value) >= parseAmount(values.amountFrom);
                                              },
                                          )
                                    : Yup.string().nullable(),
                        }),
                    )}
                    onSubmit={(values) => {
                        const section = values.activeSection || filters.section;
                        const { status, statusAllSelected } = resolveStatusFilter(values.status, section);
                        const newFilters = {
                            ...filters,
                            reference: values.reference,
                            amountFrom: values.amountFrom,
                            amountTo: values.amountTo,
                            orderBy: values.orderBy,
                            order: values.order,
                            creationDateFrom: values.creationDates[0],
                            creationDateTo: values.creationDates[1],
                            paymentDateFrom: values.paymentDates[0],
                            paymentDateTo: values.paymentDates[1],
                            status,
                            statusAllSelected,
                            currency: values.currency,
                            section,
                        };
                        setFilters(newFilters);

                        if (!isDesktop) {
                            dispatch(checksActions.clearEcheqsControl());
                        }
                    }}
                    disabledFilterButton={false}
                    setFormikProps={() => {}}
                    orderByProps={{
                        propList: [
                            { value: "paymentDate", label: i18n.get("echeqs.control.orderBy.paymentDate.label") },
                        ],
                        defaultValueSelector: filters.orderBy || "paymentDate",
                        defaultValueRadioButtons: filters.order === "ASC" ? "ascendent" : "descendent",
                        direction: "row",
                        onChange: (values, value) => {
                            const newFilters = {
                                ...filters,
                                orderBy: value.orderBy,
                                order: value.direction === "ascendent" ? "ASC" : "DESC",
                            };
                            setFilters(newFilters);

                            if (!isDesktop) {
                                dispatch(checksActions.clearEcheqsControl());
                            }
                        },
                    }}
                    emittedEnabled={emittedSectionEnabled}
                    enableSelectMultpleStatus={multipleStatusEnabled}
                    statusOptions={{
                        availables: getSectionStates("availables"),
                        collections: getSectionStates("collections"),
                        liquidations: getSectionStates("liquidations"),
                        ...(emittedSectionEnabled ? { emitted: getSectionStates("emitted") } : {}),
                    }}
                    minCreationDateFrom={(() => {
                        const minCreationDateFrom = config.getInteger("frontend.ControlEcheqs.CreationDateFrom");
                        const minCreationDateFromDate = new Date();
                        minCreationDateFromDate.setMonth(minCreationDateFromDate.getMonth() - minCreationDateFrom);
                        return minCreationDateFromDate;
                    })()}
                    minPaymentDateFrom={(() => {
                        const maxPaymentDateFrom = config.getInteger("frontend.ControlEcheqs.PaymentDateFrom");
                        const minPaymentDateFrom = new Date();
                        minPaymentDateFrom.setMonth(minPaymentDateFrom.getMonth() - maxPaymentDateFrom);
                        return minPaymentDateFrom;
                    })()}
                    maxPaymentDateTo={(() => {
                        const maxPaymentDateTo = config.getInteger("frontend.ControlEcheqs.PaymentDateTo");
                        const minPaymentDateTo = new Date();
                        minPaymentDateTo.setMonth(minPaymentDateTo.getMonth() + maxPaymentDateTo);
                        return minPaymentDateTo;
                    })()}
                    maxLength={maxLength}
                    enableUSD={echeqUSDfuncionalites}
                />
            </Box>
        </>
    );
}

const mapStateToProps = (state) => ({
    echeqs: checksSelectors.getEcheqsControlList(state),
    loading: checksSelectors.getEcheqsControlLoading(state),
    lastUpdate: checksSelectors.getEcheqsControlLastUpdate(state),
    requestDate: checksSelectors.getEcheqsControlRequestDate(state),
    totalCount: checksSelectors.getEcheqsControlTotalCount(state),
    totalAmount: checksSelectors.getEcheqsControlTotalAmount(state),
    totalPages: checksSelectors.getEcheqsControlTotalPages(state),
    pageNumber: checksSelectors.getEcheqsControlPageNumber(state),
    isFetching: checksSelectors.getEcheqsControlIsFetching(state),
    hasCacheRequest: checksSelectors.getEcheqsControlHasCacheRequest(state),
    downloadLoading: checksSelectors.getEcheqsControlDownloadLoading(state),
});

EcheqsControl.propTypes = {
    dispatch: func.isRequired,
    echeqs: arrayOf(shape({})).isRequired,
    loading: bool.isRequired,
    isDesktop: bool.isRequired,
    lastUpdate: shape({}),
    requestDate: shape({}),
    totalCount: number.isRequired,
    totalAmount: number.isRequired,
    totalPages: number.isRequired,
    pageNumber: number.isRequired,
    isFetching: bool.isRequired,
    hasCacheRequest: bool.isRequired,
    downloadLoading: bool.isRequired,
    embeddedInV3Shell: bool,
};

EcheqsControl.defaultProps = {
    lastUpdate: null,
    requestDate: null,
    embeddedInV3Shell: false,
};

export default compose(connect(mapStateToProps), resizableRoute)(EcheqsControl);
